import { isDeepEqual } from "@via/utils/helpers.tsx";
import { useCallback, useEffect, useRef, type EffectCallback } from "react";

type DependencyList = ReadonlyArray<unknown>;

/**
 * `useDeepCompareEffect` is a custom React hook that works like `useEffect` but performs a deep comparison
 * of the dependencies array. This ensures that the effect is only re-run when the actual content of the
 * dependencies changes, rather than when their references change.
 *
 * @param effect - The effect callback function that is executed when the dependencies change.
 * @param dependencies - An array of dependencies that are deeply compared on each render.
 *
 * This hook is useful when you have dependencies that are objects or arrays, which are compared by reference
 * in `useEffect`. By using deep comparison, you avoid unnecessary re-renders and effect executions when the
 * dependencies' references change but their content remains the same.
 *
 * Example usage:
 *
 * const MyComponent: React.FC<MyComponentProps> = ({ obj, arr }) => {
 *   useDeepCompareEffect(() => {
 *     // Effect logic here
 *   }, [obj, arr]);
 *
 *   return (
 *     <div>
 *       {/* Component JSX here * /}
 *     </div>
 *   );
 * };
 */

export const useDeepCompareEffect = (
  effect: EffectCallback,
  dependencies: DependencyList
): void => {
  const ref = useRef<DependencyList | undefined>(undefined);

  if (!isDeepEqual(ref.current, dependencies)) {
    ref.current = dependencies;
  }

  useEffect(effect, [ref.current && JSON.stringify(ref.current)]);
};

/**
 * `useDeepCompareCallback` is a custom hook that works like `useCallback` but performs a deep comparison
 * of the dependencies array. This ensures that the callback is only re-created when the actual content
 * of the dependencies changes, rather than when their references change.
 *
 * @param callback - The callback function that is memoized.
 * @param dependencies - An array of dependencies that are deeply compared.
 * @returns A memoized version of the callback function that only changes when dependencies change.
 */
export const useDeepCompareCallback = (
  callback: (value: Record<string, any>) => void,
  dependencies: DependencyList
) => {
  const ref = useRef<DependencyList | undefined>(undefined);
  if (!isDeepEqual(ref.current, dependencies)) {
    ref.current = dependencies;
  }

  return useCallback(callback, [ref.current && JSON.stringify(ref.current)]);
};
